import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeftRight, RefreshCw, CheckCircle2, AlertTriangle, Boxes, Scale, TrendingUp } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, PeriodPicker, BranchFilter } from "../../components/finance";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function SyncBadge({ inSync, t }) {
  return inSync
    ? <Badge tone="success" data-testid="recon-sync-badge"><CheckCircle2 size={13} /> {t("In Sync")}</Badge>
    : <Badge tone="warn" data-testid="recon-sync-badge"><AlertTriangle size={13} /> {t("Needs Sync")}</Badge>;
}

function Row({ label, value, testid, strong, tone }) {
  const toneCls = tone === "danger" ? "text-danger-text" : tone === "success" ? "text-success-text" : "text-ink";
  return (
    <div className="flex items-center justify-between border-b border-line/60 py-2 last:border-0">
      <span className="text-sm text-ink-soft">{label}</span>
      <span className={`text-sm ${strong ? "font-extrabold" : "font-semibold"} ${toneCls}`} data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Reconciliation() {
  const { t } = useT();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [invSyncing, setInvSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance/reconciliation", { params: { period, branch_id: branchId || undefined } }); setData(data); }
    catch { toast.error(t("Failed to load")); }
    finally { setLoading(false); }
  }, [period, branchId, t, toast]);
  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    setSyncing(true);
    try { await api.post("/finance/reconciliation/resync"); toast.success(t("Ledger resynced")); await load(); }
    catch { toast.error(t("Resync failed")); }
    finally { setSyncing(false); }
  };

  const syncInventoryGL = async () => {
    if (!window.confirm(t("Post an opening-balance adjustment journal (1300 ↔ 3400) to close the WAC vs GL gap? This action is recorded in the audit trail."))) return;
    setInvSyncing(true);
    try {
      const { data } = await api.post("/finance/reconciliation/sync-inventory");
      if (data.posted) toast.success(t("Adjustment journal posted — inventory is now in sync with the GL"));
      else toast.info?.(data.message) || toast.success(data.message);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("Failed")); }
    finally { setInvSyncing(false); }
  };

  const inv = data?.inventory;
  const cogs = data?.cogs;
  const sync = data?.sync;

  return (
    <div data-testid="reconciliation-page">
      <PageHeader title={t("Financial Reconciliation")} subtitle={t("Verify the financial layer is fully in sync with operations")} icon={ArrowLeftRight}>
        <BranchFilter value={branchId} onChange={setBranchId} />
        <PeriodPicker value={period} onChange={setPeriod} />
        {hasPermission?.("manage_accounting") && (
          <Button size="sm" onClick={resync} disabled={syncing} data-testid="recon-resync-btn">
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {t("Sync Now")}
          </Button>
        )}
      </PageHeader>

      {loading || !data ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          {/* Sync status banner */}
          <Card className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="recon-sync-status">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink/5 text-ink"><RefreshCw size={18} /></div>
              <div>
                <p className="text-sm font-bold text-ink">{t("General Ledger Synchronization")}</p>
                <p className="text-xs text-ink-mute" data-testid="recon-last-sync">{t("Last synchronized")}: {fmtDateTime(sync?.last_sync_at)}{sync?.last_sync_duration_ms != null ? ` · ${sync.last_sync_duration_ms} ms` : ""}</p>
              </div>
            </div>
            <SyncBadge inSync={sync?.in_sync} t={t} />
          </Card>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* Inventory reconciliation */}
            <Card className="p-5" data-testid="recon-inventory-card">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2"><Boxes size={18} className="text-primary" /><h3 className="font-heading text-base font-bold text-ink">{t("Inventory Valuation vs General Ledger")}</h3></div>
                <SyncBadge inSync={inv?.in_sync} t={t} />
              </div>
              <Row label={t("Inventory Valuation (WAC)")} value={rp(inv?.wac_total)} testid="recon-inv-wac" strong />
              <div className="pl-3">
                <Row label={t("— Central Warehouse")} value={rp(inv?.wac_central)} testid="recon-inv-wac-central" />
                <Row label={t("— Branches")} value={rp(inv?.wac_branch)} testid="recon-inv-wac-branch" />
              </div>
              <Row label={t("Inventory Value in General Ledger")} value={rp(inv?.gl_total)} testid="recon-inv-gl" strong />
              <div className="pl-3">
                {inv?.gl_by_account?.map((a) => (
                  <Row key={a.account_code} label={`${a.account_code} · ${a.account_name}`} value={rp(a.amount)} testid={`recon-inv-gl-${a.account_code}`} />
                ))}
              </div>
              <div className="mt-2 rounded-xl bg-ink/5 p-3">
                <Row label={t("Difference")} value={rp(inv?.difference)} testid="recon-inv-diff" strong tone={inv?.in_sync ? "success" : "danger"} />
                <p className="mt-1 text-xs text-ink-mute">{t("Differences arise where opening stock or inter-branch transfers are not yet journaled. Finance only observes — it never alters operational stock.")}</p>
                {!inv?.in_sync && hasPermission?.("manage_accounting") && (
                  <Button size="sm" variant="outline" className="mt-2 w-full" onClick={syncInventoryGL} disabled={invSyncing} data-testid="recon-inv-sync-btn">
                    <Scale size={14} className={invSyncing ? "animate-pulse" : ""} /> {t("Post Opening Balance Adjustment (close gap)")}
                  </Button>
                )}
              </div>
            </Card>

            {/* COGS reconciliation */}
            <Card className="p-5" data-testid="recon-cogs-card">
              <div className="mb-3 flex items-center gap-2"><Scale size={18} className="text-primary" /><h3 className="font-heading text-base font-bold text-ink">{t("COGS: Theoretical vs Actual (WAC)")}</h3></div>
              <Row label={t("Theoretical COGS (recipe × price)")} value={rp(cogs?.theoretical_cogs)} testid="recon-cogs-theoretical" strong />
              <Row label={t("Actual COGS (recipe × WAC)")} value={rp(cogs?.actual_wac_cogs)} testid="recon-cogs-actual" strong />
              <Row label={t("General Ledger COGS")} value={rp(cogs?.gl_cogs)} testid="recon-cogs-gl" />
              <div className="mt-2 rounded-xl bg-ink/5 p-3">
                <Row label={t("Variance")} value={`${rp(cogs?.variance)}${cogs?.variance_pct != null ? ` (${cogs.variance_pct}%)` : ""}`} testid="recon-cogs-variance" strong tone={Math.abs(cogs?.variance || 0) < 1 ? "success" : "danger"} />
                <p className="mt-1 text-xs text-ink-mute">{t("Recipe coverage")}: {cogs?.coverage_pct != null ? `${cogs.coverage_pct}%` : "—"}. {t("Items with WAC cost of 0 need ingredient costing (e.g. produced/beverage bases).")}</p>
              </div>
            </Card>
          </div>

          {/* Top variance items */}
          {cogs?.top_variance_items?.length > 0 && (
            <Card className="overflow-hidden p-0" data-testid="recon-variance-table">
              <div className="flex items-center gap-2 border-b border-line px-5 py-3"><TrendingUp size={16} className="text-primary" /><h3 className="font-heading text-sm font-bold text-ink">{t("Top Cost Variance by Menu")}</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-left text-xs font-bold uppercase tracking-wide text-ink-mute">
                    <tr><th className="px-5 py-2">{t("Menu")}</th><th className="px-3 py-2 text-right">{t("Qty")}</th><th className="px-3 py-2 text-right">{t("Theoretical")}</th><th className="px-3 py-2 text-right">{t("Actual (WAC)")}</th><th className="px-5 py-2 text-right">{t("Variance")}</th></tr>
                  </thead>
                  <tbody>
                    {cogs.top_variance_items.map((m) => (
                      <tr key={m.menu_item_id} className="border-t border-line/60" data-testid={`recon-variance-row-${m.menu_item_id}`}>
                        <td className="px-5 py-2 font-semibold text-ink">{m.menu_item_name || m.menu_item_id}</td>
                        <td className="px-3 py-2 text-right text-ink-soft">{m.qty}</td>
                        <td className="px-3 py-2 text-right text-ink-soft">{rp(m.theoretical)}</td>
                        <td className="px-3 py-2 text-right text-ink-soft">{rp(m.actual)}</td>
                        <td className={`px-5 py-2 text-right font-bold ${Math.abs(m.variance) < 1 ? "text-success-text" : "text-danger-text"}`}>{rp(m.variance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* GL module sync status */}
          <Card className="overflow-hidden p-0" data-testid="recon-module-table">
            <div className="border-b border-line px-5 py-3"><h3 className="font-heading text-sm font-bold text-ink">{t("Module Synchronization Status")}</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs font-bold uppercase tracking-wide text-ink-mute">
                  <tr><th className="px-5 py-2">{t("Module")}</th><th className="px-3 py-2 text-right">{t("Source Records")}</th><th className="px-3 py-2 text-right">{t("Journals")}</th><th className="px-5 py-2 text-right">{t("Status")}</th></tr>
                </thead>
                <tbody>
                  {sync?.modules?.map((m) => (
                    <tr key={m.module} className="border-t border-line/60" data-testid={`recon-module-${m.module}`}>
                      <td className="px-5 py-2 font-semibold capitalize text-ink">{m.module}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{m.source_records}</td>
                      <td className="px-3 py-2 text-right text-ink-soft">{m.journals}</td>
                      <td className="px-5 py-2 text-right">{m.stale ? <Badge tone="warn">{t("Pending sync")}</Badge> : <Badge tone="success">{t("Synced")}</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
